import React, { useEffect, useState } from "react";

import {
  Box,
  Grid,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  TextFieldProps,
  Typography,
} from "@material-ui/core";
import { Label } from "../../types";
import { iconUrl } from "../store/icons";
import { iconIdAllowedWithText } from "../store/fibo";
import { useDebouncedCallback } from "use-debounce";

const useStyles = makeStyles((theme) => ({
  left: {
    "& input": {
      textAlign: "left",
    },
  },
  center: {
    "& input": {
      textAlign: "center",
    },
  },
  right: {
    "& input": {
      textAlign: "right",
    },
  },
  iconSelect: {
    display: "flex",
    flexWrap: "wrap",
    maxWidth: "320px",
    maxHeight: "400px",
    color: theme.palette.text.primary,
    "& img": {
      filter: "invert()",
    },
  },
  invertImg: {
    color: theme.palette.text.primary,
    "& img": {
      filter: "invert()",
    },
  },
  disabled: {
    opacity: "20%",
  },
  denseSelect: {
    padding: "0 5px",
  },
}));

interface IconSelectProps {
  icons: { id: string; name: string }[];
  currentIconId: string;
  onIconSelect: (id: string) => void;
  disabled?: boolean;
}
export const IconSelect = ({
  icons,
  currentIconId,
  onIconSelect,
  disabled,
}: IconSelectProps) => {
  const classes = useStyles();

  if (icons.findIndex((i) => i.id === currentIconId) === -1) currentIconId = "";

  return (
    <Select
      className={classes.invertImg}
      classes={{ root: classes.denseSelect }}
      MenuProps={{ classes: { list: classes.iconSelect } }}
      value={currentIconId}
      onChange={(ev) => onIconSelect(ev.target.value as string)}
      displayEmpty={true}
      disabled={disabled}
      variant="outlined"
    >
      {icons.map((icon) => (
        <MenuItem value={icon.id} key={icon.id} title={icon.name}>
          <img
            src={iconUrl(icon.id)}
            width="30px"
            height="30px"
            alt={icon.name}
          ></img>
        </MenuItem>
      ))}
    </Select>
  );
};

export const DebouncedTextField = (props: TextFieldProps) => {
  const [localValue, setLocalValue] = useState<unknown>(props.value);

  const onChange = props.onChange ?? (() => {});

  useEffect(() => {
    setLocalValue(props.value);
  }, [props.value]);

  const debouncedOnChange = useDebouncedCallback(onChange, 300).callback;

  return (
    <TextField
      {...props}
      value={localValue}
      onChange={(ev) => {
        setLocalValue(ev.target.value);
        ev.persist(); // See: https://reactjs.org/docs/legacy-event-pooling.html, can be removed in React 17
        debouncedOnChange(ev);
      }}
    />
  );
};

interface LabelInputProps {
  label: Label;
  labelChanged: (v: Partial<Label>) => void;
  caption: string;
  icons: { id: string; name: string }[];
}
const LabelInput = ({
  label,
  labelChanged,
  caption,
  icons,
}: LabelInputProps) => {
  const classes = useStyles();

  if (label.leftText || label.rightText)
    icons = icons.filter((ic) => iconIdAllowedWithText(ic.id));

  const sideDisabled =
    !iconIdAllowedWithText(label.leftIconId) ||
    !iconIdAllowedWithText(label.rightIconId) ||
    !!label.centerText;
  const centerDisabled = !!label.leftText || !!label.rightText;

  return (
    <Box>
      <Box mb={1} mt={2}>
        <Typography variant="caption" color={"textSecondary"}>
          {caption}
        </Typography>
      </Box>
      <Grid container spacing={1}>
        <IconSelect
          icons={icons}
          currentIconId={label.leftIconId}
          onIconSelect={(id) => labelChanged({ leftIconId: id })}
        />
        <Grid item xs>
          <DebouncedTextField
            size="small"
            variant="outlined"
            label="Left"
            className={`${classes.left} ${
              sideDisabled ? classes.disabled : ""
            }`}
            fullWidth
            value={label.leftText}
            onChange={(ev) => labelChanged({ leftText: ev.target.value })}
            disabled={sideDisabled}
          />
        </Grid>
        <Grid item xs>
          <DebouncedTextField
            className={`${classes.center} ${
              centerDisabled ? classes.disabled : ""
            }`}
            size="small"
            variant="outlined"
            label="Center"
            fullWidth
            value={label.centerText}
            onChange={(ev) => labelChanged({ centerText: ev.target.value })}
            disabled={centerDisabled}
          />
        </Grid>
        <Grid item xs>
          <DebouncedTextField
            size="small"
            variant="outlined"
            label="Right"
            className={`${classes.right} ${
              sideDisabled ? classes.disabled : ""
            }`}
            fullWidth
            value={label.rightText}
            onChange={(ev) => labelChanged({ rightText: ev.target.value })}
            disabled={sideDisabled}
          />
        </Grid>
        <IconSelect
          icons={icons}
          currentIconId={label.rightIconId}
          onIconSelect={(id) => labelChanged({ rightIconId: id })}
        />
      </Grid>
    </Box>
  );
};

export default LabelInput;
